// v3
// Path: frontend/src/components/QuickBooksSync/DryRunDiffModal.jsx
// Inputs: token, config, onClose, onSyncCompleted
// Outputs: React JSX modal allowing real-time diff preview, detailed connection info display, and pre-sync confirmed live synchronization
// Summary: Dry-run and live execution inspector comparing PBS ERP tables to QuickBooks Online with connection details breakdown, diff analysis, and pre-flight sync execution confirmation.

import React, { useState, useEffect } from 'react';

export const DryRunDiffModal = ({ token, config, onClose, onSyncCompleted }) => {
    const [recordLimit, setRecordLimit] = useState(50);
    const [activeTab, setActiveTab] = useState('diff'); // 'diff' or 'source'
    const [loadingPbs, setLoadingPbs] = useState(false);
    const [loadingDiff, setLoadingDiff] = useState(false);
    const [loadingSync, setLoadingSync] = useState(false);
    const [pbsPreviewData, setPbsPreviewData] = useState(null);
    const [diffPreviewData, setDiffPreviewData] = useState(null);
    const [diffError, setDiffError] = useState(null);
    const [syncResult, setSyncResult] = useState(null);
    const [showConfirmSync, setShowConfirmSync] = useState(false);

    // Parse SourceConnPrefix into readable components
    const parsedConn = (() => {
        const p = config.SourceConnPrefix;
        if (!p || !p.startsWith('[')) return null;
        const cb = p.indexOf(']');
        if (cb === -1) return null;
        const parts = p.substring(1, cb).split('|');
        if (parts.length === 5) {
            return {
                serverName: parts[0],
                ip: parts[1],
                instance: parts[2],
                port: parts[3],
                user: parts[4]
            };
        }
        return null;
    })();

    // Auto-run diff when modal opens
    useEffect(() => {
        handleRunDryRun();
    }, [config]);

    const handleFetchPbsSample = async () => {
        setLoadingPbs(true);
        setPbsPreviewData(null);
        setActiveTab('source');
        try {
            const params = new URLSearchParams({
                sourceDatabaseName: config.SourceDatabaseName,
                entityType: config.EntityType,
                limit: String(recordLimit)
            });
            if (config.SourceConnPrefix) {
                params.append('sourceConnPrefix', config.SourceConnPrefix);
            }
            if (config.TableName) {
                params.append('tableName', config.TableName);
            }

            const res = await fetch(`/api/quickbooks/pbs-preview?${params.toString()}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.error || 'Failed to fetch PBS source sample');
            }

            const data = await res.json();
            setPbsPreviewData(data);
        } catch (err) {
            alert(`PBS Fetch Error: ${err.message}`);
        } finally {
            setLoadingPbs(false);
        }
    };

    const handleRunDryRun = async () => {
        setLoadingDiff(true);
        setDiffPreviewData(null);
        setDiffError(null);
        setSyncResult(null);
        setActiveTab('diff');
        try {
            const payload = {
                configId: config.ConfigID,
                accountId: config.AccountID,
                sourceConnPrefix: config.SourceConnPrefix,
                sourceDatabaseName: config.SourceDatabaseName,
                tableName: config.TableName,
                targetQBEntity: config.TargetQBEntity,
                entityType: config.EntityType,
                limit: recordLimit
            };

            const res = await fetch('/api/quickbooks/sync/preview', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.error || 'Dry run preview failed');
            }

            const data = await res.json();
            setDiffPreviewData(data);
        } catch (err) {
            console.error('Diff error:', err);
            setDiffError(err.message);
        } finally {
            setLoadingDiff(false);
        }
    };

    const handleOpenSyncConfirmation = () => {
        setShowConfirmSync(true);
    };

    const handleExecuteLiveSyncConfirmed = async () => {
        setShowConfirmSync(false);
        setLoadingSync(true);
        setSyncResult(null);
        try {
            const payload = {
                configId: config.ConfigID,
                accountId: config.AccountID,
                sourceConnPrefix: config.SourceConnPrefix,
                sourceDatabaseName: config.SourceDatabaseName,
                tableName: config.TableName,
                targetQBEntity: config.TargetQBEntity,
                entityType: config.EntityType,
                limit: recordLimit
            };

            const res = await fetch('/api/quickbooks/sync/execute', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.error || 'Live sync execution failed');
            }

            const data = await res.json();
            setSyncResult(data);
            if (onSyncCompleted) onSyncCompleted();
        } catch (err) {
            alert(`Sync Execution Error: ${err.message}`);
        } finally {
            setLoadingSync(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 border border-slate-700 rounded-xl max-w-5xl w-full max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="p-5 border-b border-slate-700 flex justify-between items-center bg-slate-800/90">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-600/20 border border-blue-500/40 flex items-center justify-center text-blue-400">
                            <span className="material-symbols-outlined">preview</span>
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h3 className="text-base font-bold text-white">{config.ConfigName}</h3>
                                <span className="px-2 py-0.5 rounded text-[11px] font-mono bg-blue-950 text-blue-300 border border-blue-800">
                                    {config.EntityType}
                                </span>
                            </div>
                            <p className="text-xs text-slate-400">
                                Source: <code className="text-slate-300">{config.SourceDatabaseName}</code> &rarr; Target QB: <span className="text-emerald-300">{config.AccountName || 'QuickBooks'}</span>
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-white p-1 rounded">
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>

                {/* Connection & Target Settings Card */}
                <div className="bg-slate-900/90 border-b border-slate-700/80 px-5 py-3">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                        <div className="space-y-0.5">
                            <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Source SQL Instance</span>
                            <div className="text-white font-medium flex items-center gap-1">
                                <span className="material-symbols-outlined text-[14px] text-emerald-400">dns</span>
                                <span className="truncate">{parsedConn ? `${parsedConn.serverName} (${parsedConn.ip}:${parsedConn.port})` : (config.SourceConnPrefix || 'Local SQL')}</span>
                            </div>
                            <div className="text-[11px] text-slate-400 font-mono">
                                Instance: {parsedConn?.instance || 'ssinstance'} &bull; User: {parsedConn?.user || 'sa'}
                            </div>
                        </div>

                        <div className="space-y-0.5">
                            <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Database & Source Table</span>
                            <div className="text-white font-medium flex items-center gap-1 font-mono">
                                <span className="material-symbols-outlined text-[14px] text-sky-400">table_chart</span>
                                <span className="text-sky-300 font-bold">{config.SourceDatabaseName}</span>
                            </div>
                            <div className="text-[11px] text-slate-300 font-mono">
                                Table: <strong className="text-emerald-300">{config.TableName || config.EntityType}</strong>
                            </div>
                        </div>

                        <div className="space-y-0.5">
                            <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">QuickBooks Destination</span>
                            <div className="text-white font-medium flex items-center gap-1">
                                <span className="material-symbols-outlined text-[14px] text-purple-400">account_balance</span>
                                <span className="truncate">{config.AccountName || 'Connected Account'}</span>
                            </div>
                            <div className="text-[11px] text-slate-400">
                                Target Entity: <strong className="text-purple-300">{config.TargetQBEntity || config.EntityType}</strong> &bull; <span className="uppercase text-[10px] px-1 py-0.2 bg-slate-800 rounded">{config.Environment || 'sandbox'}</span>
                            </div>
                        </div>

                        <div className="space-y-0.5">
                            <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Pipeline Strategy</span>
                            <div className="text-slate-300 flex items-center gap-1 font-medium">
                                <span className="material-symbols-outlined text-[14px] text-amber-400">sync_alt</span>
                                <span>{config.SyncDirection || 'PBS_TO_QB'}</span>
                            </div>
                            <div className="text-[11px] text-slate-400">
                                Policy: <strong className="text-slate-300">{config.ConflictPolicy || 'SOURCE_WINS'}</strong>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Control Bar */}
                <div className="p-4 bg-slate-900/80 border-b border-slate-700/80 flex flex-wrap items-center justify-between gap-3 text-xs">
                    <div className="flex items-center gap-2">
                        <span className="text-slate-400">Batch Limit:</span>
                        <select
                            value={recordLimit}
                            onChange={e => setRecordLimit(parseInt(e.target.value, 10))}
                            className="bg-slate-900 border border-slate-700 rounded px-2.5 py-1 text-white focus:outline-none focus:border-blue-500 font-medium"
                        >
                            <option value={10}>10 records</option>
                            <option value={50}>50 records</option>
                            <option value={100}>100 records</option>
                            <option value={500}>500 records</option>
                        </select>

                        <div className="flex bg-slate-800 border border-slate-700 rounded p-0.5 ml-2">
                            <button
                                onClick={() => setActiveTab('diff')}
                                className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${activeTab === 'diff' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-400 hover:text-white'}`}
                            >
                                Dry-Run Diff
                            </button>
                            <button
                                onClick={handleFetchPbsSample}
                                className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${activeTab === 'source' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-400 hover:text-white'}`}
                            >
                                Raw PBS Table
                            </button>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleRunDryRun}
                            disabled={loadingDiff}
                            className="px-3.5 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded text-xs font-semibold flex items-center gap-1 transition-colors"
                        >
                            <span className="material-symbols-outlined text-[15px]">refresh</span>
                            {loadingDiff ? 'Diffing...' : 'Re-Run Diff'}
                        </button>
                        <button
                            onClick={handleOpenSyncConfirmation}
                            disabled={loadingSync}
                            className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded text-xs font-bold flex items-center gap-1.5 transition-colors shadow-sm"
                        >
                            <span className="material-symbols-outlined text-[15px]">bolt</span>
                            {loadingSync ? 'Syncing...' : 'Execute Live Sync'}
                        </button>
                    </div>
                </div>

                {/* Content View */}
                <div className="p-5 overflow-y-auto flex-1 space-y-4">
                    {/* Live Sync Result Banner */}
                    {syncResult && (
                        <div className={`p-4 rounded-lg border text-xs ${syncResult.status === 'SUCCESS' ? 'bg-emerald-950/60 border-emerald-800 text-emerald-200' : 'bg-amber-950/60 border-amber-800 text-amber-200'}`}>
                            <div className="flex justify-between items-start">
                                <div>
                                    <h4 className="font-bold text-sm flex items-center gap-1.5">
                                        <span className="material-symbols-outlined text-[18px]">
                                            {syncResult.status === 'SUCCESS' ? 'check_circle' : 'warning'}
                                        </span>
                                        Execution Status: {syncResult.status} ({syncResult.durationMs}ms)
                                    </h4>
                                    <p className="mt-1">{syncResult.summary}</p>
                                </div>
                                <div className="flex gap-2 text-[11px] font-mono">
                                    <span className="px-2 py-0.5 rounded bg-emerald-900/60 text-emerald-300 border border-emerald-700">
                                        +{syncResult.recordsInserted} Created
                                    </span>
                                    <span className="px-2 py-0.5 rounded bg-blue-900/60 text-blue-300 border border-blue-700">
                                        {syncResult.recordsUpdated} Updated
                                    </span>
                                    {syncResult.recordsFailed > 0 && (
                                        <span className="px-2 py-0.5 rounded bg-red-900/60 text-red-300 border border-red-700">
                                            {syncResult.recordsFailed} Failed
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Error Banner */}
                    {diffError && (
                        <div className="p-4 rounded-lg border bg-red-950/70 border-red-800 text-red-200 text-xs">
                            <div className="flex items-start gap-2">
                                <span className="material-symbols-outlined text-[20px] text-red-400">error</span>
                                <div>
                                    <h4 className="font-bold text-sm text-red-300">Diff Preview Failed</h4>
                                    <p className="mt-1 font-mono text-[11px] text-red-200/90">{diffError}</p>
                                    <p className="mt-2 text-slate-300">
                                        Tip: Ensure the Database Profile ({config.SourceConnPrefix || 'Default'}) is online and reachable, and the database <code>{config.SourceDatabaseName}</code> contains the ERP tables.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'diff' ? (
                        loadingDiff ? (
                            <div className="p-16 text-center text-slate-400">
                                <span className="material-symbols-outlined animate-spin text-3xl mb-2 text-blue-400">sync</span>
                                <p>Analyzing PBS ERP records against QuickBooks Online records...</p>
                            </div>
                        ) : diffPreviewData ? (
                            <div className="space-y-4">
                                {/* Stats Cards */}
                                <div className="grid grid-cols-4 gap-3">
                                    <div className="bg-slate-900/70 border border-slate-700/70 rounded-lg p-3 text-center">
                                        <div className="text-xl font-bold text-white">{diffPreviewData.totalPbsRecords}</div>
                                        <div className="text-[11px] text-slate-400">PBS Records Read</div>
                                    </div>
                                    <div className="bg-slate-900/70 border border-slate-700/70 rounded-lg p-3 text-center">
                                        <div className="text-xl font-bold text-emerald-400">+{diffPreviewData.toCreate.length}</div>
                                        <div className="text-[11px] text-slate-400">New in QuickBooks</div>
                                    </div>
                                    <div className="bg-slate-900/70 border border-slate-700/70 rounded-lg p-3 text-center">
                                        <div className="text-xl font-bold text-blue-400">{diffPreviewData.toUpdate.length}</div>
                                        <div className="text-[11px] text-slate-400">Existing (Updates)</div>
                                    </div>
                                    <div className="bg-slate-900/70 border border-slate-700/70 rounded-lg p-3 text-center">
                                        <div className="text-xl font-bold text-slate-300">{diffPreviewData.totalQbRecords}</div>
                                        <div className="text-[11px] text-slate-400">Total in QuickBooks</div>
                                    </div>
                                </div>

                                {/* Sample Mappings List */}
                                <div className="space-y-2">
                                    <h4 className="text-xs font-semibold text-slate-300 uppercase tracking-wider">
                                        Sample Mappings & Transformation Diff ({diffPreviewData.sampleMappings.length})
                                    </h4>
                                    <div className="space-y-2">
                                        {diffPreviewData.sampleMappings.map((sample, idx) => (
                                            <div key={idx} className="bg-slate-900/90 border border-slate-700/80 rounded-lg p-3 text-xs space-y-2">
                                                <div className="flex justify-between items-center">
                                                    <div className="flex items-center gap-2">
                                                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${sample.action === 'CREATE' ? 'bg-emerald-950 text-emerald-300 border border-emerald-800' : 'bg-blue-950 text-blue-300 border border-blue-800'}`}>
                                                            {sample.action}
                                                        </span>
                                                        <span className="font-mono font-semibold text-white">{sample.sourceKey}</span>
                                                        <span className="text-slate-400">&bull;</span>
                                                        <span className="text-slate-200 font-medium">{sample.sourceName}</span>
                                                    </div>
                                                </div>
                                                <pre className="p-2.5 bg-slate-950 rounded text-[11px] font-mono text-blue-300 overflow-x-auto border border-slate-800">
                                                    {JSON.stringify(sample.mappedPayload, null, 2)}
                                                </pre>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="p-12 text-center text-slate-400">
                                Click "Re-Run Diff" to analyze transformations.
                            </div>
                        )
                    ) : (
                        loadingPbs ? (
                            <div className="p-16 text-center text-slate-400">
                                <span className="material-symbols-outlined animate-spin text-3xl mb-2 text-blue-400">sync</span>
                                <p>Extracting raw table records from PBS ERP database...</p>
                            </div>
                        ) : pbsPreviewData ? (
                            <div className="space-y-3">
                                <div className="text-xs text-slate-400">
                                    Extracted <strong>{pbsPreviewData.count}</strong> rows from table <code className="text-blue-300">{pbsPreviewData.table}</code>
                                </div>
                                <div className="border border-slate-700 rounded-lg overflow-x-auto max-h-96">
                                    <table className="w-full text-left text-xs text-slate-300">
                                        <thead className="bg-slate-900 text-slate-400 font-semibold sticky top-0 border-b border-slate-700">
                                            <tr>
                                                {pbsPreviewData.records.length > 0 && Object.keys(pbsPreviewData.records[0]).map(col => (
                                                    <th key={col} className="p-2.5 font-mono whitespace-nowrap">{col}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-700/60 font-mono text-[11px]">
                                            {pbsPreviewData.records.map((row, rIdx) => (
                                                <tr key={rIdx} className="hover:bg-slate-750">
                                                    {Object.values(row).map((val, cIdx) => (
                                                        <td key={cIdx} className="p-2.5 whitespace-nowrap text-slate-200">
                                                            {val !== null && val !== undefined ? String(val) : <span className="text-slate-600">NULL</span>}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ) : (
                            <div className="p-12 text-center text-slate-400">
                                Click "Raw PBS Table" to query sample records from the SQL database.
                            </div>
                        )
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-slate-700 flex justify-end bg-slate-800/90">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded text-sm transition-colors"
                    >
                        Close Inspector
                    </button>
                </div>
            </div>

            {/* Pre-Sync Execution Confirmation Modal */}
            {showConfirmSync && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4 backdrop-blur-sm">
                    <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-lg w-full shadow-2xl overflow-hidden p-6 space-y-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-amber-500/20 border border-amber-500/40 flex items-center justify-center text-amber-400 shrink-0">
                                <span className="material-symbols-outlined text-2xl">warning</span>
                            </div>
                            <div>
                                <h4 className="text-base font-bold text-white">Confirm Live QuickBooks Synchronization</h4>
                                <p className="text-xs text-slate-400">Please verify connection parameters before writing to QuickBooks Online.</p>
                            </div>
                        </div>

                        <div className="bg-slate-950 border border-slate-800 rounded-lg p-3 space-y-2 text-xs">
                            <div className="flex justify-between py-1 border-b border-slate-800">
                                <span className="text-slate-400">Source Server Instance:</span>
                                <span className="text-slate-200 font-mono font-semibold">{parsedConn ? `${parsedConn.serverName} (${parsedConn.ip}:${parsedConn.port})` : config.SourceConnPrefix}</span>
                            </div>
                            <div className="flex justify-between py-1 border-b border-slate-800">
                                <span className="text-slate-400">Database & Table:</span>
                                <span className="text-sky-300 font-mono font-semibold">[{config.SourceDatabaseName}].[dbo].[{config.TableName || config.EntityType}]</span>
                            </div>
                            <div className="flex justify-between py-1 border-b border-slate-800">
                                <span className="text-slate-400">Target QuickBooks Account:</span>
                                <span className="text-purple-300 font-semibold">{config.AccountName || 'QuickBooks Account'}</span>
                            </div>
                            <div className="flex justify-between py-1 border-b border-slate-800">
                                <span className="text-slate-400">Destination Entity:</span>
                                <span className="text-purple-200 font-bold">{config.TargetQBEntity || config.EntityType}</span>
                            </div>
                            <div className="flex justify-between py-1 border-b border-slate-800">
                                <span className="text-slate-400">Batch Record Limit:</span>
                                <span className="text-white font-bold">{recordLimit} records</span>
                            </div>
                            <div className="flex justify-between py-1">
                                <span className="text-slate-400">Conflict Policy / Direction:</span>
                                <span className="text-amber-300">{config.ConflictPolicy || 'SOURCE_WINS'} ({config.SyncDirection || 'PBS_TO_QB'})</span>
                            </div>
                        </div>

                        {diffPreviewData && (
                            <div className="p-3 bg-emerald-950/40 border border-emerald-800/60 rounded-lg text-xs space-y-1">
                                <div className="text-emerald-300 font-semibold flex items-center gap-1.5">
                                    <span className="material-symbols-outlined text-[16px]">insights</span>
                                    <span>Diff Analysis Ready:</span>
                                </div>
                                <div className="text-slate-300 pl-5">
                                    &bull; <strong>{diffPreviewData.toCreate?.length || 0}</strong> record(s) will be <strong>CREATED</strong> in QuickBooks<br/>
                                    &bull; <strong>{diffPreviewData.toUpdate?.length || 0}</strong> record(s) will be <strong>UPDATED</strong> in QuickBooks<br/>
                                    &bull; <strong>{diffPreviewData.totalPbsRecords || 0}</strong> total record(s) scanned from PBS
                                </div>
                            </div>
                        )}

                        <div className="flex justify-end gap-3 pt-2">
                            <button
                                type="button"
                                onClick={() => setShowConfirmSync(false)}
                                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-xs font-semibold transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleExecuteLiveSyncConfirmed}
                                disabled={loadingSync}
                                className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 transition-colors shadow-lg shadow-emerald-900/30"
                            >
                                <span className="material-symbols-outlined text-[16px]">bolt</span>
                                {loadingSync ? 'Executing Live Sync...' : 'Confirm & Execute Live Sync'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
